import { fonts } from '@/constants/fonts';
import { useThemeColor } from '@/hooks/useThemeColor';
import React, { ReactNode } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  type ImageSourcePropType,
  type ViewStyle,
  type StyleProp,
} from 'react-native';
import ThemedText from './ThemedText';

type CardVariant = 'elevated' | 'outlined' | 'filled';

interface CardProps {
  children?: ReactNode;
  variant?: CardVariant;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;

  // Header
  title?: string;
  subtitle?: string;
  headerImage?: ImageSourcePropType;
  headerRight?: ReactNode;

  // Cover Image
  coverImage?: ImageSourcePropType;
  coverImageHeight?: number;

  // Footer
  footer?: ReactNode;
}

export default function Card({
  children,
  variant = 'elevated',
  onPress,
  style,
  disabled = false,
  title,
  subtitle,
  headerImage,
  headerRight,
  coverImage,
  coverImageHeight = 200,
  footer,
}: CardProps) {
  const backgroundColor = useThemeColor({}, 'backgroundSecondary');
  const borderColor = useThemeColor({}, 'border');
  const textColor = useThemeColor({}, 'text');
  const textSecondaryColor = useThemeColor({}, 'textSecondary');

  const cardStyle: ViewStyle = {
    backgroundColor,
    ...getVariantStyle(variant, borderColor),
  };

  const Container = onPress ? TouchableOpacity : View;

  return (
    <Container
      style={[styles.card, cardStyle, disabled && styles.disabled, style]}
      onPress={onPress}
      disabled={disabled || !onPress}
      activeOpacity={0.7}
    >
      {/* Cover Image */}
      {coverImage && (
        <Image
          source={coverImage}
          style={[styles.coverImage, { height: coverImageHeight }]}
          resizeMode="cover"
        />
      )}

      {/* Header */}
      {(title || subtitle || headerImage || headerRight) && (
        <View style={styles.header}>
          {headerImage && (
            <Image source={headerImage} style={styles.headerImage} />
          )}
          <View style={styles.headerText}>
            {title && (
              <ThemedText style={styles.title} numberOfLines={1}>
                {title}
              </ThemedText>
            )}
            {subtitle && (
              <ThemedText
                style={[styles.subtitle, { color: textSecondaryColor }]}
                numberOfLines={2}
              >
                {subtitle}
              </ThemedText>
            )}
          </View>
          {headerRight && <View style={styles.headerRight}>{headerRight}</View>}
        </View>
      )}

      {/* Content */}
      {children && <View style={styles.content}>{children}</View>}

      {/* Footer */}
      {footer && <View style={styles.footer}>{footer}</View>}
    </Container>
  );
}

function getVariantStyle(variant: CardVariant, borderColor: string): ViewStyle {
  switch (variant) {
    case 'elevated':
      return {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 4,
      };
    case 'outlined':
      return {
        borderWidth: 1,
        borderColor,
      };
    case 'filled':
      return {};
  }
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    overflow: 'hidden',
    marginBottom: 16,
  },
  disabled: {
    opacity: 0.5,
  },
  coverImage: {
    width: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  headerImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontFamily: fonts.bold,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    fontFamily: fonts.regular,
  },
  headerRight: {
    marginLeft: 12,
  },
  content: {
    padding: 16,
    paddingTop: 0,
  },
  footer: {
    padding: 16,
    paddingTop: 0,
    flexDirection: 'row',
    gap: 8,
  },
});
